import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Menu } from '../../components/Menu';
/**
 * Wrap item onClick handlers so the menu auto-closes for action items.
 * Checkbox items stay open so the user can toggle multiple settings.
 */
function withAutoClose(items, close) {
    return items.map((item) => {
        if (item.type === 'action') {
            const orig = item.onClick;
            return { ...item, onClick: () => { orig(); close(); } };
        }
        return item;
    });
}
/**
 * Generic three-dot overflow menu for viewer toolbars (document, text).
 *
 * Renders a vertical-ellipsis button styled as a `hekate-player-btn` and
 * opens a `Menu` dropdown above the toolbar. The caller is responsible for
 * building the items list; this component is viewer-agnostic.
 *
 * Action items auto-close the menu on click. Checkbox items stay open so
 * the user can toggle multiple settings without reopening the menu.
 *
 * Visibility is controlled by CSS container queries on the wrapper element
 * (the caller should add a class like `hekate-dv-overflow-btn` and a
 * corresponding `@container` rule).
 */
export const ViewerToolbarOverflowMenu = React.memo(function ViewerToolbarOverflowMenu({ items }) {
    const [isOpen, setIsOpen] = useState(false);
    const btnRef = useRef(null);
    const close = useCallback(() => setIsOpen(false), []);
    const wrappedItems = useMemo(() => withAutoClose(items, close), [items, close]);
    if (items.length === 0)
        return null;
    return (_jsxs("div", { style: { position: 'relative' }, children: [_jsx("button", { ref: btnRef, className: "hekate-player-btn", onClick: () => setIsOpen((v) => !v), "aria-label": "More options", "aria-haspopup": "menu", "aria-expanded": isOpen, "data-tooltip": "More", type: "button", children: _jsx("span", { className: "hekate-viewer-overflow-dots", children: "\u22EE" }) }), _jsx(Menu, { items: wrappedItems, isOpen: isOpen, onClose: close, ariaLabel: "More options", triggerRef: btnRef, className: "hekate-toolbar-overflow-menu" })] }));
});
